// MObywatelVerification.tsx
import { Button, Progress } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { firestore } from '../../../../../firebase/firebase';
import { FirestoreCollection } from '../../../../../firebase/firebase.models';

import { MObywatelInstructions } from './MObywatelInstructions';
import {
  handleContinueVerification,
  requestMObywatelVerification,
  showMObywatelInstructions,
} from './mobywatelService';

interface MObywatelVerificationProps {
  show: boolean;
  userUid: string;
  firstName: string;
  lastName: string;
  email: string;
}

export const MObywatelVerification: React.FC<MObywatelVerificationProps> = ({
  show,
  userUid,
  firstName,
  lastName,
  email,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [requestStatus, setRequestStatus] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState<string | null>(null);
  const [codeProvidedAt, setCodeProvidedAt] = useState<Date | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);

  useEffect(() => {
    if (!userUid) return;

    // Listen for the latest request for this user
    const unsubscribe = firestore
      .collection(FirestoreCollection.MOBYWATEL_REQUESTS)
      .where('userUid', '==', userUid)
      .orderBy('createdAt', 'desc')
      .limit(1)
      .onSnapshot((snapshot) => {
        if (!snapshot.empty) {
          const latestRequest = snapshot.docs[0].data();
          setRequestStatus(latestRequest.status);
          setVerificationCode(latestRequest.verificationCode || null);

          // Set codeProvidedAt for countdown
          if (latestRequest.codeProvidedAt) {
            setCodeProvidedAt(latestRequest.codeProvidedAt.toDate());
          } else {
            setCodeProvidedAt(null);
          }
        }
      });

    return () => unsubscribe();
  }, [userUid]);

  // Countdown timer for progress bar in code_provided status
  useEffect(() => {
    if (requestStatus !== 'code_provided' || !codeProvidedAt) {
      setTimeRemaining(0);
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const elapsed = Math.floor((now.getTime() - codeProvidedAt.getTime()) / 1000);
      const remaining = Math.max(0, 180 - elapsed); // 3 minutes = 180 seconds
      setTimeRemaining(remaining);

      // Frontend locally changes status to code_expired when countdown reaches 0
      if (remaining === 0 && requestStatus === 'code_provided') {
        setRequestStatus('code_expired');
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [requestStatus, codeProvidedAt]);

  const formatTimeRemaining = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleShowInstructions = async () => {
    setIsLoading(true);
    try {
      await showMObywatelInstructions({ userUid, firstName, lastName, email });
      // Status change will be handled by realtime listener
    } catch (error) {
      console.error('Error showing mObywatel instructions:', error);
      // Error handling will be added in future iterations
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestCode = async () => {
    setIsLoading(true);
    try {
      await requestMObywatelVerification({ userUid, firstName, lastName, email });
      // Status change will be handled by realtime listener
    } catch (error) {
      console.error('Error requesting mObywatel verification:', error);
      // Error handling will be added in future iterations
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = async () => {
    setIsLoading(true);
    try {
      await handleContinueVerification(userUid, true);
      // Status change will be handled by realtime listener
    } catch (error) {
      console.error('Error continuing verification:', error);
      // Error handling will be added in future iterations
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    setIsLoading(true);
    try {
      await handleContinueVerification(userUid, false);
      // Status change will be handled by realtime listener
    } catch (error) {
      console.error('Error canceling verification:', error);
      // Error handling will be added in future iterations
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    // Show modal when user needs to confirm continuation
    if (requestStatus === 'awaiting_user_response') {
      return (
        <>
          <div className="text-center mb-6">
            <div className="text-orange-600 text-lg font-semibold mb-2">
              ⏰ {t('MOBYWATEL_VERIFICATION.SESSION_EXPIRING_TITLE')}
            </div>
            <p className="text-gray-600 text-sm mb-4">{t('MOBYWATEL_VERIFICATION.SESSION_EXPIRING_DESCRIPTION')}</p>
          </div>
          <div className="flex justify-between space-x-3">
            <Button type="default" onClick={handleCancel} className="flex-1">
              {t('MOBYWATEL_VERIFICATION.CANCEL_BUTTON')}
            </Button>
            <Button
              type="primary"
              loading={isLoading}
              onClick={handleContinue}
              className="flex-1 bg-gradient-to-r from-green-500 to-blue-500 border-none">
              {t('MOBYWATEL_VERIFICATION.CONTINUE_BUTTON')}
            </Button>
          </div>
        </>
      );
    }

    // Show inactive state
    if (requestStatus === 'user_inactive') {
      return (
        <>
          <div className="text-center mb-4">
            <div className="text-gray-600 text-lg font-semibold mb-2">
              💤 {t('MOBYWATEL_VERIFICATION.SESSION_INACTIVE_TITLE')}
            </div>
            <p className="text-gray-500 text-sm">{t('MOBYWATEL_VERIFICATION.SESSION_INACTIVE_DESCRIPTION')}</p>
          </div>
          <div className="flex justify-center">
            <Button type="primary" loading={isLoading} onClick={handleShowInstructions}>
              {t('MOBYWATEL_VERIFICATION.START_AGAIN_BUTTON')}
            </Button>
          </div>
        </>
      );
    }

    if (requestStatus === 'instructions_shown') {
      return (
        <>
          <MObywatelInstructions variant="full" className="mb-6" />
          <div className="flex justify-end">
            <Button
              type="primary"
              loading={isLoading}
              onClick={handleRequestCode}
              className="bg-gradient-to-r from-green-500 to-blue-500 border-none rounded-lg h-9 font-medium hover:from-green-600 hover:to-blue-600 hover:-translate-y-0.5 transition-all duration-200">
              {t('MOBYWATEL_VERIFICATION.GENERATE_CODE_BUTTON')}
            </Button>
          </div>
        </>
      );
    }

    if (requestStatus === 'waiting_for_code') {
      return (
        <>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            {t('MOBYWATEL_VERIFICATION.WAITING_DESCRIPTION')}
          </p>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          </div>
        </>
      );
    }

    if (requestStatus === 'code_expired') {
      return (
        <>
          <div className="text-center mb-4">
            <div className="text-red-600 text-lg font-semibold mb-2">
              ⏰ {t('MOBYWATEL_VERIFICATION.CODE_EXPIRED_TITLE')}
            </div>
            <p className="text-gray-600 text-sm">{t('MOBYWATEL_VERIFICATION.CODE_EXPIRED_DESCRIPTION')}</p>
          </div>
          <div className="flex justify-end">
            <Button type="primary" loading={isLoading} onClick={handleRequestCode}>
              {t('MOBYWATEL_VERIFICATION.GENERATE_CODE_AGAIN_BUTTON')}
            </Button>
          </div>
        </>
      );
    }

    if (requestStatus === 'code_provided' && verificationCode) {
      const progressPercent = Math.max(0, (timeRemaining / 180) * 100);
      const isNearExpiry = timeRemaining <= 60; // Last minute warning

      return (
        <>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            {t('MOBYWATEL_VERIFICATION.CODE_PROVIDED_DESCRIPTION')}
          </p>
          <div className="text-center mb-4">
            <div className="text-3xl font-bold text-blue-600 bg-blue-50 p-4 rounded-lg inline-block mb-3">
              {verificationCode}
            </div>

            {codeProvidedAt && (
              <div className="mb-3">
                <div className="flex justify-center items-center space-x-2 mb-2"></div>
                <div className="mb-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-600">{t('MOBYWATEL_VERIFICATION.CODE_EXPIRES_IN')}</span>
                    <span className={`text-xs font-semibold ${isNearExpiry ? 'text-red-600' : 'text-blue-600'}`}>
                      {formatTimeRemaining(timeRemaining)}
                    </span>
                  </div>
                  <Progress
                    percent={progressPercent}
                    status={progressPercent > 0 ? (isNearExpiry ? 'exception' : 'active') : 'exception'}
                    showInfo={false}
                    strokeColor={isNearExpiry ? '#ff4d4f' : '#1890ff'}
                    size="small"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Instrukcje w eleganckim, kompaktowym stylu */}
          <div className="mt-4 p-3 bg-gradient-to-br from-blue-50 to-green-50 border border-blue-200 rounded-lg">
            <MObywatelInstructions variant="compact" />
          </div>
        </>
      );
    }

    // Default state - show verification button
    return (
      <>
        <p className="text-gray-600 text-sm leading-relaxed mb-4">{t('MOBYWATEL_VERIFICATION.DESCRIPTION')}</p>
        <div className="flex justify-end">
          <Button
            type="primary"
            loading={isLoading}
            onClick={handleShowInstructions}
            className="bg-gradient-to-r from-blue-500 to-green-500 border-none rounded-lg h-9 font-medium hover:from-blue-600 hover:to-green-600 hover:-translate-y-0.5 transition-all duration-200">
            {t('MOBYWATEL_VERIFICATION.BUTTON')}
          </Button>
        </div>
      </>
    );
  };

  if (!show) {
    return null;
  }

  return (
    <div className="mb-4 p-5 rounded-xl bg-gradient-to-br from-blue-50 to-green-50 border border-blue-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
      <div className="flex items-center mb-3">
        <div className="w-10 h-10 mr-3">
          <img src="/images/assets/mobywatel.png" alt="mObywatel" className="w-full h-full object-contain rounded-lg" />
        </div>
        <h3 className="text-gray-800 font-semibold text-base leading-tight mb-0">
          {t('MOBYWATEL_VERIFICATION.HEADING')}
        </h3>
      </div>
      {renderContent()}
    </div>
  );
};
